import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, TextInput, Select, Switch, Textarea, Label } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { useCustomField, updateCustomField } from './useData';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

const FieldConfigurator: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const fieldId = id ? parseInt(id, 10) : null;
  const { data: field, isLoading, mutate } = useCustomField(fieldId || 0);

  const [options, setOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [validation, setValidation] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (field) {
      setOptions(Array.isArray(field.options) ? field.options : []);
      setValidation(field.validation || null);
    }
  }, [field]);

  const handleAddOption = () => {
    if (newOption.trim()) {
      setOptions([...options, newOption.trim()]);
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!field) return;

    setIsSaving(true);
    try {
      await updateCustomField(field.id, {
        ...field,
        options: options.length > 0 ? options : undefined,
        validation: validation || undefined,
      });
      await mutate();
      showSuccess('Custom field updated successfully');
    } catch (error: any) {
      showError(error.message || 'Failed to update custom field');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" message="Loading custom field..." />
      </div>
    );
  }

  if (!field) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className={cn('text-2xl font-bold mb-2', colors.text)}>Custom field not found</h2>
          <p className={cn('mb-4', colors.textSecondary)}>The custom field you are looking for does not exist.</p>
          <Button onClick={() => navigate('/custom-fields')}>Back to Custom Fields</Button>
        </div>
      </div>
    );
  }

  const showOptions = field.fieldType === 'select' || field.fieldType === 'checkbox';
  const showValidation = field.fieldType === 'text' || field.fieldType === 'number' || field.fieldType === 'textarea';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className={cn('text-3xl font-bold mb-2', colors.text)}>Configure: {field.fieldLabel}</h1>
        <p className={colors.textSecondary}>{field.entityType} - {field.fieldType}</p>
      </div>

      <div className={cn('rounded-lg shadow-sm border p-6 space-y-6', colors.surface, colors.border)}>
        {/* Options Configuration */}
        {showOptions && (
          <div>
            <Label>
              Options {field.fieldType === 'select' ? '(for dropdown)' : '(for checkbox)'}
            </Label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <TextInput
                    value={option}
                    onChange={(e) => {
                      const updated = [...options];
                      updated[index] = e.target.value;
                      setOptions(updated);
                    }}
                    className="flex-1"
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveOption(index)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <TextInput
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder="Enter option value"
                  className="flex-1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
                <Button variant="outline" onClick={handleAddOption}>
                  Add Option
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Validation Rules */}
        {showValidation && (
          <div>
            <Label>Validation Rules</Label>
            <div className="space-y-4">
              {field.fieldType === 'text' || field.fieldType === 'textarea' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className={cn('text-xs font-medium mb-1', colors.textMuted)}>Min Length</Label>
                      <TextInput
                        type="number"
                        value={validation?.minLength || ''}
                        onChange={(e) =>
                          setValidation({
                            ...validation,
                            minLength: e.target.value ? parseInt(e.target.value) : undefined,
                          })
                        }
                        placeholder="Minimum length"
                      />
                    </div>
                    <div>
                      <Label className={cn('text-xs font-medium mb-1', colors.textMuted)}>Max Length</Label>
                      <TextInput
                        type="number"
                        value={validation?.maxLength || ''}
                        onChange={(e) =>
                          setValidation({
                            ...validation,
                            maxLength: e.target.value ? parseInt(e.target.value) : undefined,
                          })
                        }
                        placeholder="Maximum length"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className={cn('text-xs font-medium mb-1', colors.textMuted)}>Pattern (Regex)</Label>
                    <TextInput
                      value={validation?.pattern || ''}
                      onChange={(e) =>
                        setValidation({
                          ...validation,
                          pattern: e.target.value || undefined,
                        })
                      }
                      placeholder="e.g., ^[A-Za-z]+$"
                    />
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={cn('text-xs font-medium mb-1', colors.textMuted)}>Min Value</Label>
                    <TextInput
                      type="number"
                      value={validation?.min || ''}
                      onChange={(e) =>
                        setValidation({
                          ...validation,
                          min: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      placeholder="Minimum value"
                    />
                  </div>
                  <div>
                    <Label className={cn('text-xs font-medium mb-1', colors.textMuted)}>Max Value</Label>
                    <TextInput
                      type="number"
                      value={validation?.max || ''}
                      onChange={(e) =>
                        setValidation({
                          ...validation,
                          max: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      placeholder="Maximum value"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Preview */}
        <div>
          <Label>Preview</Label>
          <div className={cn('border rounded-lg p-4', colors.headerBg, colors.border)}>
            <Label className="mb-1" required={field.isRequired}>
              {field.fieldLabel}
            </Label>
            {field.fieldType === 'text' && (
              <TextInput placeholder="Enter value" disabled />
            )}
            {field.fieldType === 'number' && (
              <TextInput type="number" placeholder="Enter number" disabled />
            )}
            {field.fieldType === 'date' && (
              <TextInput type="date" disabled />
            )}
            {field.fieldType === 'select' && (
              <Select
                disabled
                value=""
                onChange={() => {}}
                options={[
                  { value: '', label: 'Select an option' },
                  ...options.map((option) => ({ value: option, label: option })),
                ]}
              />
            )}
            {field.fieldType === 'checkbox' && (
              <Switch disabled />
            )}
            {field.fieldType === 'textarea' && (
              <Textarea rows={3} placeholder="Enter text" disabled />
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex items-center justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/custom-fields')}>
          Cancel
        </Button>
        <Button variant="default" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>
    </div>
  );
};

export default FieldConfigurator;
